import { Component, EventEmitter, Input, Output, SimpleChanges } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IGeofence } from "@app/interfaces/IGeofences";
import { AlertCategory } from "@app/main/modules/alerts/_enums/alerts.enums";
import { IAlertRule, IAlertRuleGroupMapping } from "@app/main/modules/alerts/_models/alerts.interfaces";
import { UserRole } from "@app/services/accountFeature.service";
import { GroupService } from "@app/services/groups/group.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { IGroup } from "../../_models/IGroup";
import { IGroupWithIds } from "../../_models/IGroupWithIds";
import './group-details.component.scss';

@UntilDestroy()
@Component({
    selector: 'group-details',
    template: require('./group-details.component.html')
})
export class GroupDetailComponent {
    @Input() group: IGroupWithIds;
    @Input() alertRuleGroupMappings: IAlertRuleGroupMapping;
    @Input() existingDepotVehicleMatchesFound: boolean;

    @Output() groupChange = new EventEmitter<IGroupWithIds>();
    @Output() newSubGroup = new EventEmitter<IGroupWithIds>();
    @Output() depotStatusChanged = new EventEmitter<void>();

    groupForm: FormGroup;
    unchangedGroup: IGroupWithIds;

    parentDepot: IGroupWithIds;
    selectedParentGroupId: number;
    childDepots: IGroupWithIds[] = [];
    numParents: number;
    selectedGeofence: IGeofence;

    // AlertRuleTable data
    tableColumns: IVTableColumn<IAlertRule>[];
    tableRows: IVTableRow[];

    UserRole = UserRole;

    get formIsValid(): boolean {
        return this.groupForm?.valid && !this.existingDepotVehicleMatchesFound;
    }

    constructor(
        private readonly groupService: GroupService,
        private readonly vTableService: VTableService) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes['group']?.currentValue && changes['group'].currentValue.Id !== changes['group'].previousValue?.Id) {
            this.initialise();            
        }

        if (changes['alertRuleGroupMappings']) {
            this.tableRows = this.vTableService.createTableRows(this.alertRuleGroupMappings?.AlertRules, this.tableColumns);
        }
    }

    selectParentGroup(group: IGroup): void {
        this.selectedParentGroupId = group?.Id || null;
        this.groupForm.controls.ParentId.setValue(group?.Id);
    }

    addSubGroup(): void {
        const group = this.groupService.getNewGroupWithIds();

        if (this.group && this.group.Id && this.groupService.sumParents(this.group) < 5 && this.group.Id > 0) {
            group.parent = this.group;
            group.ParentId = this.group.Id;
        }

        this.group = group;
        this.createForm();
        
        this.newSubGroup.emit(group);
    }

    onGeofenceSelected(geofence: IGeofence): void {
        this.selectedGeofence = geofence;
        this.groupForm.get('GeofenceId').setValue(geofence.Id);
    }
    
    groupHasChanged(): boolean {
        return this.groupForm.get("Name").value != this.unchangedGroup.Name ||
            this.groupForm.get("IsDepot").value != this.unchangedGroup.IsDepot ||
            this.groupForm.get("ParentId").value != this.unchangedGroup.ParentId ||
            this.groupForm.get("GeofenceId").value != this.unchangedGroup.GeofenceId;
    }

    updateUnchangedGroup(): void {
        this.unchangedGroup = this.group;
    }

    private clearAllValues(): void {
        this.groupForm = null;
        this.parentDepot = null;
        this.childDepots = [];
        this.numParents = 0;
        this.selectedParentGroupId = null;
    }

    private initialise(): void {
        this.tableRows = [];
        this.clearAllValues();
        this.createForm();
        this.createTableColumns();

        this.unchangedGroup = { ...this.group };

        if (!this.group.IsDepot && this.group.parent) {
            this.checkParentIsDepot(this.group);
        }

        if (!this.group.IsDepot && !this.parentDepot && this.group.children) {
            this.childDepots = [];
            this.checkChildIsDepot(this.group);
        }

        this.numParents = this.groupService.sumParents(this.group);
        this.selectedParentGroupId = this.group.ParentId;
    }

    private createTableColumns(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Alerts.AlertName" }),
                createCellFunction: (alertRule) => this.vTableService.createTableCell(alertRule.Name),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Alerts.AlertCategory" }),
                createCellFunction: (alertRule) => this.vTableService.createEnumCell(AlertCategory, "AlertCategory", alertRule.Category),
            },
        ]
    }
    
    private async createForm(): Promise<void> {
        const groups = await this.groupService.getGroupsWithIds();
        const hierarchy = await this.groupService.getGroupsHierarchy();

        const parentIdValidators = [
            CustomFormValidators.parentMustNotBeSameAsChild(this.group),
            CustomFormValidators.groupCanBeUsedAsParent(this.group, groups),       
            CustomFormValidators.vehiclesMustNotBeInDepotIfParentsAreDepot(this.group, hierarchy)     
        ];

        this.groupForm = new FormGroup({
            Name: new FormControl(this.group.Name, {
                validators: [
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(60),
                    CustomFormValidators.duplicateProperty(groups.filter(g => g.Id !== this.group.Id), 'Name', 'groupNameDuplicated')
                ]
            }),
            IsDepot: new FormControl(this.group.IsDepot),
            ParentId: new FormControl(this.group.ParentId, parentIdValidators),
            GeofenceId: new FormControl(this.group.GeofenceId)
        })

        this.groupForm.valueChanges.pipe(untilDestroyed(this)).subscribe({
            next: values => {
                this.group = {
                    ...this.group,
                    Name: values.Name,
                    IsDepot: values.IsDepot,
                    ParentId: values.ParentId,
                    GeofenceId: values.GeofenceId
                }

                // this.group looks to be passed in by value not ref,
                // so if this.group.IsDepot changes to true, these aren't propogated to the validator unless we reset it
                this.groupForm.controls["ParentId"].setValidators(parentIdValidators);

                this.group.parent = groups.find(g => g.Id == values.ParentId);

                this.groupChange.emit(this.group);
            }
        })
    }

    private checkParentIsDepot(group: IGroupWithIds): void {
        if (group.IsDepot) {
            this.parentDepot = group;
            return;
        }

        if (group.parent) {
            this.checkParentIsDepot(group.parent);
        }
    }

    private checkChildIsDepot(group: IGroupWithIds): void {
        if (group.IsDepot) {
            this.childDepots.push(group);
            return;
        }

        if (group.children) {
            group.children.forEach(child => {
                this.checkChildIsDepot(child);
            });
        }
    }
}
